import { useId, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { buildSceneBlocks, findSceneBlockAt } from './sceneStripBlocks';

import type { Scene } from '@src/editor/models/layout/Scene';
import type { EditorElement } from '@src/utility/sceneAnchors';

/** Height of the strip itself, excluding the control bar's 6px row gap. */
export const SCENE_STRIP_HEIGHT = 32;

/**
 * What the strip adds to the control bar's height: the strip plus the gap the
 * bar's flex column puts above it. Reserved (empty) wherever the strip is not
 * drawn, so switching playback modes never resizes the modal.
 */
export const SCENE_STRIP_ROW_HEIGHT = SCENE_STRIP_HEIGHT + 6;

/**
 * Gap between blocks. The strip's own background shows through it, so the
 * scene break reads as a drawn line rather than as an absence.
 */
const SCENE_SEAM_WIDTH = 2;

interface Props {
  /** Scenes of the clip as it is currently being edited. */
  scenes: readonly Scene[];
  /**
   * The clip's timeline elements — scene anchors are resolved against these.
   * `editor.timeline.allElements`; the clip's editor store has no `tracksMap`.
   */
  elements: readonly EditorElement[];
  /** Clip duration, from the same playback bridge that scales the rail above. */
  duration: number;
  currentTime: number;
  onSeek: (time: number) => void;
}

/**
 * Read-only strip of scene blocks under the clip preview's player (PLAT-11236).
 * Fit-to-width: every block's share of the strip is its share of the clip, so
 * the strip and the progress rail share one scale and one coordinate space.
 *
 * Nothing here mutates a scene — no command, no save, no undo entry.
 */
export const ClipSceneStrip = ({
  scenes,
  elements,
  duration,
  currentTime,
  onSeek,
}: Props) => {
  // Per instance: the clip preview's strip and the reframe modal's can be
  // mounted at once, and a shared id would fire both tooltips off one hover.
  const tooltipId = useId();

  const blocks = useMemo(
    () => buildSceneBlocks(scenes, elements, duration),
    [scenes, elements, duration],
  );

  const activeSceneId = findSceneBlockAt(blocks, currentTime)?.id;

  const playheadPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Strip data-testid="clip-scene-strip" $seams={blocks.length > 0}>
      {blocks.map((block, index) => (
        <SceneBlock
          key={block.id}
          type="button"
          data-testid="clip-scene-block"
          data-scene-id={block.id}
          $active={block.id === activeSceneId}
          style={{
            left: `${block.leftPct}%`,
            // The seam is the gap before the *next* block, so the last one
            // keeps its full width — trimmed, it leaves a sliver of the strip's
            // background hanging at the right edge with nothing beyond it, and
            // ends short of the playhead at the end of the clip.
            width:
              index === blocks.length - 1
                ? `${block.widthPct}%`
                : `calc(${block.widthPct}% - ${SCENE_SEAM_WIDTH}px)`,
          }}
          onClick={() => onSeek(block.seekTo)}
          aria-label={block.ariaLabel}
          aria-current={block.id === activeSceneId ? 'true' : undefined}
          data-tooltip-id={tooltipId}
          data-tooltip-content={block.label}
        />
      ))}
      {blocks.length > 0 && (
        <Playhead
          data-testid="clip-scene-strip-playhead"
          style={{ left: `${playheadPct}%` }}
          aria-hidden="true"
        />
      )}
      {/* Portaled past the strip's own `overflow: hidden`, which would
          otherwise clip the card to a 32px row. */}
      {ReactDOM.createPortal(
        <Tooltip
          id={tooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </Strip>
  );
};

export default ClipSceneStrip;

/**
 * Fills whatever box it is given — the caller owns the coordinate space. In
 * the control bar that box bleeds past the padding to match the rail; in the
 * reframe modal it is the width of the crop area.
 */
const Strip = styled.div<{ $seams: boolean }>`
  position: relative;
  width: 100%;
  height: ${SCENE_STRIP_HEIGHT}px;
  flex-shrink: 0;
  border-radius: 3px;
  overflow: hidden;
  /* Shows only in the gaps the blocks leave, which is what draws the scene
     breaks. Painted only when there are blocks to leave gaps — otherwise the
     strip would be one solid bar of separator colour. */
  background: ${({ $seams }) => ($seams ? 'var(--border)' : 'transparent')};
`;

const SceneBlock = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: ${SCENE_SEAM_WIDTH}px;
  padding: 0;
  margin: 0;
  border: none;
  /* Square: the strip carries the rounding, so blocks butt up against the
     seam instead of letting it bleed round their corners. */
  background: ${({ $active }) =>
    $active ? 'var(--popover)' : 'var(--surface)'};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--popover);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
`;

const Playhead = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: var(--foreground);
  border-radius: 1px;
  pointer-events: none;
`;
